/**
 * Gravatar utility functions
 * Generate Gravatar URLs from email addresses using MD5 hashing
 */

import md5 from 'md5';

/**
 * Generate Gravatar URL from email address
 * Uses MD5 hash of normalized email with identicon fallback
 *
 * @param email - User email address
 * @param size - Image size in pixels (default: 200)
 * @returns Gravatar URL
 *
 * @example
 * ```typescript
 * const avatarUrl = getGravatarUrl('user@example.com');
 * // Returns: https://www.gravatar.com/avatar/{md5}?s=200&d=identicon
 * ```
 */
export function getGravatarUrl(email: string, size: number = 200): string {
  // Handle empty email
  if (!email) {
    return `https://www.gravatar.com/avatar/00000000000000000000000000000000?s=${size}&d=identicon`;
  }

  // Normalize email (trim whitespace and convert to lowercase)
  const normalized = email.toLowerCase().trim();

  // Generate MD5 hash using md5 library
  const hash = md5(normalized);

  // Return Gravatar URL with identicon fallback
  // d=identicon generates a unique geometric pattern if no Gravatar exists
  return `https://www.gravatar.com/avatar/${hash}?s=${size}&d=identicon`;
}
